import React, { useContext, useState, useRef, useEffect } from 'react'
import moment from 'moment'
import { Portal } from '@gorhom/portal'
import { Formik } from 'formik'

import Box, { BoxProps } from '@components/kit/box'
import Text from '@components/kit/text'
import Button from '@components/kit/button'
import DateTimePicker, {
  ImperativeDateTimePickerProps
} from '@components/kit/datetime-picker'
import Form from '@components/kit/form'
import Pressable from '@components/kit/pressable'
import ScrollView from '@components/kit/scroll-view'
import { ChatContext } from '../../chat.web'
import { useMedsStore } from '@store/Meds'
import { TextFieldFormated } from '@components/formFields'
import Loading from '@components/kit/loading'

const HorizontalRule = ({
  marginY = '20',
  height = '2px',
  width = '100%',
  style = {},
  ...props
}: BoxProps) => (
  <Box
    width={width}
    height={height}
    marginY={marginY as any}
    style={{ backgroundColor: '#F9F9F9', ...(style as any) }}
    {...props}
  />
)

const Row = ({ children }) => (
  <Box flexDirection='row' justifyContent='space-between' alignItems='center'>
    {children}
  </Box>
)

const LeftColumn = ({ children }) => (
  <Box justifyContent='flex-start' alignItems='center'>
    <Text font='display.14.bold'>{children}</Text>
  </Box>
)

const RightColumn = ({ children }) => (
  <Box justifyContent='flex-end' alignItems='center'>
    {children}
  </Box>
)
interface CustomNativeEventLayout {
  top: number
  left: number
  width: number
  height: number
}

interface StopMedicationProps {
  medication: any
  onCancel: () => void
  onConfirm: () => void
  hasClosed: boolean
}

function StopMedication({
  medication,
  onCancel,
  onConfirm,
  hasClosed
}: StopMedicationProps) {
  const [errorMessage, setErrorMessage] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { userId } = useContext(ChatContext)
  const endDatePickerRef = React.createRef<ImperativeDateTimePickerProps>()
  const [endDate, setEndDate] = useState('')
  const [endReason, setEndReason] = useState('')
  const [modalRootPosition, setModalRootPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  })
  const [endDatePickerPosition, setEndDatePickerPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0
  })

  const onChangeEndReason = (text: string) => {
    setErrorMessage(null)
    setEndReason(text)
  }

  const onChangeEndDate = (text: string) => {
    setErrorMessage(null)
    setEndDate(text)
  }

  const openEndDatePicker = () => {
    endDatePickerRef?.current.present()
  }

  const handleSetEndDate = (date: moment.Moment) => {
    setErrorMessage(null)
    setEndDate(date.utc().format('MM/DD/YYYY'))
  }

  const handleConfirm = () => {
    if (!medication || !endDate) {
      setErrorMessage('Please fill out all required fields')
      return
    }
    setIsSubmitting(true)
    useMedsStore
      .getState()
      .stopMedication(
        userId,
        medication.auto_id,
        !!endDate ? moment(endDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
        endReason
      )
      .then(success => {
        if (success) {
          onConfirm()
          onCancel()
        } else {
          setErrorMessage(useMedsStore.getState().error)
        }
      })
      .catch(error => {
        console.log('error', error)
        setErrorMessage('Failed to stop medication')
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const getCustomLayoutDimensions = (nativeEvent: any) => {
    const layout = nativeEvent.layout as any as CustomNativeEventLayout
    return {
      top: layout.top || 0,
      left: layout.left || 0,
      bottom: (layout.top || 0) + (layout.height || 0),
      right: (layout.left || 0) + (layout.width || 0)
    }
  }

  useEffect(() => {
    if (hasClosed) {
      setEndDate('')
      setEndReason('')
      setErrorMessage(null)
      setIsSubmitting(false)
    }
  }, [hasClosed])

  return (
    <Box
      overflowY='hidden'
      flexDirection='column'
      justifyContent='center'
      mt='24'
      style={{ flexShrink: 1, maxHeight: '84vh' }}
    >
      <Box
        style={{ flex: 1, overflow: 'hidden' }}
        onLayout={({ nativeEvent }) =>
          setModalRootPosition(getCustomLayoutDimensions(nativeEvent))
        }
      >
        <Text variant='h3' mb='24' textAlign='center'>
          When did the member stopped taking{' '}
          {medication?.drug_name || 'the medication'}?
        </Text>

        <ScrollView overflowY='auto'>
          <Formik
            initialValues={{ medication: '' }}
            onSubmit={() => {}}
            validateOnChange={false}
          >
            {({ handleSubmit }) => (
              <Form>
                <Box flexDirection='column-reverse'>
                  <Row>
                    <LeftColumn>Reason</LeftColumn>
                    <RightColumn>
                      <TextFieldFormated
                        name='endReason'
                        label={null}
                        value={endReason}
                        valuePlaceholder='Write something...'
                        onChangeText={onChangeEndReason}
                      />
                    </RightColumn>
                  </Row>
                  <HorizontalRule marginY='08' />
                  <Row>
                    <LeftColumn>End date*</LeftColumn>
                    <RightColumn>
                      <Pressable onPress={openEndDatePicker}>
                        <TextFieldFormated
                          name='endDate'
                          label={null}
                          value={endDate}
                          valuePlaceholder='MM/DD/YYYY'
                          onChangeText={onChangeEndDate}
                          onLayout={({ nativeEvent }) =>
                            setEndDatePickerPosition(
                              getCustomLayoutDimensions(nativeEvent)
                            )
                          }
                        />
                      </Pressable>
                      <Portal hostName='stopMedHost'>
                        <DateTimePicker
                          position='absolute'
                          top={
                            endDatePickerPosition.top - modalRootPosition.top
                          }
                          left={
                            endDatePickerPosition.right -
                            modalRootPosition.left +
                            20
                          }
                          onSubmit={handleSetEndDate}
                          ref={endDatePickerRef}
                          maxDate={moment()}
                          zIndex={999}
                          hostName='stopMedOverlayHost'
                        />
                      </Portal>
                    </RightColumn>
                  </Row>
                </Box>
              </Form>
            )}
          </Formik>
        </ScrollView>
        {errorMessage && (
          <Text
            font='display.12.bold'
            textColor='red'
            marginY='08'
            textAlign='center'
          >
            {errorMessage}
          </Text>
        )}
        <Box
          flexDirection='row'
          justifyContent='center'
          alignItems='center'
          paddingTop={errorMessage ? '00' : '32'}
          gap='12'
          paddingBottom='12'
        >
          <Button variant='tertiary' width='fit-content' onPress={onCancel}>
            Cancel
          </Button>
          <Button
            variant='violetGradient'
            width='fit-content'
            onPress={handleConfirm}
          >
            Save
          </Button>
        </Box>
      </Box>
      {isSubmitting && (
        <Portal hostName='stopMedHost'>
          <Box
            position='absolute'
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg='white'
            opacity={0.5}
            justifyContent='center'
            alignItems='center'
          />
          <Loading />
        </Portal>
      )}
    </Box>
  )
}

export default StopMedication
